import { useState, useRef, useEffect } from "react";
import useRefreshStore from "@store/useRefreshStore";

const THRESHOLD = 70;
const MAX_PULL = 130;

// disabled: 스크롤 컨테이너 자체를 제거하고 children을 그대로 렌더 (관리자 컨테이너처럼 상위가 스크롤을 담당하는 경우)
// suppressRefresh: 스크롤 컨테이너(.ptr-scroll-container)는 유지하되 당겨서 새로고침 제스처/인디케이터만 비활성화
const PullToRefresh = ({ children, disabled = false, suppressRefresh = false }) => {
    const [pullDistance, setPullDistance] = useState(0);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);

    const refs = useRef({
        startY: 0,
        pulling: false,
        distance: 0,
        refreshing: false,
    });
    const containerRef = useRef(null);
    const transitionTimerRef = useRef(null);

    useEffect(() => {
        if (disabled || suppressRefresh) return;
        const el = containerRef.current;
        if (!el) return;

        const onTouchStart = (e) => {
            if (refs.current.refreshing || disabled) return;
            refs.current.startY = e.touches[0].clientY;
            refs.current.pulling = false;
            setIsTransitioning(false);
        };

        const onTouchMove = (e) => {
            if (refs.current.refreshing || disabled) return;

            const currentY = e.touches[0].clientY;
            const dy = currentY - refs.current.startY;

            if (el.scrollTop <= 0 && dy > 10) {
                if (!refs.current.pulling) {
                    refs.current.pulling = true;
                    refs.current.startY = currentY;
                    return;
                }

                e.preventDefault();

                const rawDist = currentY - refs.current.startY;
                const dist = Math.min(
                    MAX_PULL * (1 - Math.exp(-rawDist / MAX_PULL)),
                    MAX_PULL
                );
                refs.current.distance = dist;
                setPullDistance(dist);
            } else if (refs.current.pulling && dy <= 0) {
                refs.current.pulling = false;
                refs.current.distance = 0;
                setPullDistance(0);
            }
        };

        const onTouchEnd = async () => {
            if (!refs.current.pulling || refs.current.refreshing || disabled) {
                refs.current.pulling = false;
                return;
            }

            refs.current.pulling = false;

            if (refs.current.distance >= THRESHOLD) {
                refs.current.refreshing = true;
                setIsRefreshing(true);
                setIsTransitioning(true);
                setPullDistance(THRESHOLD);

                try {
                    await new Promise((resolve) => {
                        let resolved = false;
                        const safeResolve = () => {
                            if (!resolved) {
                                resolved = true;
                                resolve();
                            }
                        };

                        const timeout = setTimeout(safeResolve, 3000);
                        useRefreshStore.getState().setRefreshResolve(() => {
                            clearTimeout(timeout);
                            safeResolve();
                        });
                        useRefreshStore.getState().triggerRefresh();
                    });
                } finally {
                    refs.current.refreshing = false;
                    refs.current.distance = 0;
                    setIsRefreshing(false);
                    setIsTransitioning(true);
                    setPullDistance(0);

                    if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current);
                    transitionTimerRef.current = setTimeout(() => setIsTransitioning(false), 300);
                }
            } else {
                refs.current.distance = 0;
                setIsTransitioning(true);
                setPullDistance(0);
                if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current);
                transitionTimerRef.current = setTimeout(() => setIsTransitioning(false), 300);
            }
        };

        el.addEventListener("touchstart", onTouchStart, { passive: true });
        el.addEventListener("touchmove", onTouchMove, { passive: false });
        el.addEventListener("touchend", onTouchEnd);

        return () => {
            el.removeEventListener("touchstart", onTouchStart);
            el.removeEventListener("touchmove", onTouchMove);
            el.removeEventListener("touchend", onTouchEnd);
            if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current);
        };
    }, [disabled, suppressRefresh]);

    if (disabled) {
        return children;
    }

    return (
        <div ref={containerRef} className="ptr-scroll-container">
            {!suppressRefresh && (
                <div
                    className={`ptr-indicator ${
                        isRefreshing ? "ptr-refreshing" : ""
                    }`}
                    style={{
                        height: pullDistance,
                        transition: isTransitioning
                            ? "height 0.3s ease"
                            : "none",
                    }}
                >
                    <div className="ptr-indicator-content">
                        {isRefreshing ? (
                            <div className="ptr-loading-spinner" />
                        ) : (
                            <div
                                className="ptr-arrow-icon"
                                style={{
                                    transform:
                                        pullDistance >= THRESHOLD
                                            ? "rotate(180deg)"
                                            : "rotate(0deg)",
                                    opacity: Math.min(pullDistance / THRESHOLD, 1),
                                }}
                            >
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                >
                                    <path
                                        d="M12 4v16M12 20l-6-6M12 20l6-6"
                                        stroke="#999"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </div>
                        )}
                    </div>
                </div>
            )}
            {children}
        </div>
    );
};

export default PullToRefresh;
